import React, { useState, useEffect } from 'react'
import eventEmitter from 'steps/shared/eventEmitter'
import { CSSTransition } from 'react-transition-group'

import { getUserType } from 'global/context/sub-contexts/userContext'

import ShareIcons from 'components/ShareIcons'

import { saveQuizResult } from 'services/personality'
import { userTypeEnum } from 'services/user'

import analytics from 'global/AnalyticsData'

import ResultSlide from './ResultSlide3'
import QuestionSlide from './QuestionSlide3'
import { sleep } from 'util/misc'
import sessionStorageWrapper from 'util/SessionStorageWrapper'

import 'steps/shared/quiz-trivia/editorial-trivia-quiz.scss'
import './quiz3.scss'
import { calculateTally, filterQuestions } from './quiz-utils'

const ANIMATION_DURATION = 400

const PersonalityQuizStep = ({
  brk,
  step,
  breakId,
  relatedBreaks,
  onEngage = function () {},
  onAwardPoints = function () {},
  onChangeBackground = function () {},
  isStarted,
  skipButton,
  nextStepButton,
  // PUCK-BACKEND-GAP (BR-5451): puck result ids are not beaker entities, so
  // saveQuizResult is skipped (see the isPuck guard below). Contract channels
  // 1+2 still work (live event + sessionStorage); cross-session persistence
  // needs a puck-aware backend. All defaulted, legacy breaks untouched.
  isPuck = false,
}) => {
  const quiz = step.personalityQuiz
  const isGuest = getUserType() === userTypeEnum.GUEST
  quiz.questions.forEach((question, i) => {
    question.position = i + 1
  })

  const [state, setState] = useState({
    position: 0,
    tally: {},
    showQuestion: true,
    showResult: false,
    result: undefined,
    component: undefined,
    questionAnswerSet: [],
    questions: quiz.questions,
  })

  const question =
    state.position < state.questions.length
      ? state.questions[state.position]
      : state.questions[0]

  const handleAnswerClick = async ({ questionId, answerId, progressBarRef }) => {
    await sleep(500) // allows radio button to appear filled in before moving on
    const answer = question.answers.find(a => a.id === answerId)

    setState(prevState => ({
      ...prevState,
      position: prevState.position,
      showQuestion: false,
    }))

    // Focus on the progress bar for screen readers and keyboard navigation
    if (progressBarRef?.current) {
      setTimeout(() => {
        progressBarRef.current.focus()
      }, 50)
    }

    const { resultId, tally } = calculateTally(
      quiz,
      state.tally,
      questionId,
      answerId,
    )
    let questionAnswerSet = JSON.parse(JSON.stringify(state.questionAnswerSet))
    questionAnswerSet.push({ questionId: questionId, answerId: answerId })

    const filteredQuestions = filterQuestions(quiz.questions, questionAnswerSet)

    analytics.stepEvent('step_interaction_pq_answer_selected', step, {
      context_id: questionId,
      context: question.text,
      result_id: answerId,
      result: answer.text,
      context_total: quiz.questions.length,
      context_position: state.position,
    })

    if (state.position < filteredQuestions.length - 1) {
      await sleep(ANIMATION_DURATION * 2 - 100)
      setState(prevState => ({
        ...prevState,
        position: prevState.position + 1,
        tally,
        questionAnswerSet,
        showQuestion: true,
        showResult: false,
        questions: filteredQuestions,
      }))
    } else {
      const result = quiz.results.find(element => element.id === resultId)
      const socialShareComponent = step.socialShareOn && (
        <ShareIcons
          stepId={step.id}
          resultId={result.id}
          shareCopy={`${result.title}: ${quiz.headline}`}
          emailConfig={{
            subject: step.shareEmailSubject,
            body: step.shareEmailBody,
          }}
          {...step}
          shareUrl={brk ? brk.shareUrl : ''}
          onShareClick={handleShareClick}
        />
      )

      await sleep(ANIMATION_DURATION)
      setState(prevState => ({
        ...prevState,
        position: prevState.position + 1,
        showQuestion: false,
        showResult: true,
        result,
        questionAnswerSet,
        component: socialShareComponent,
        questions: filteredQuestions,
      }))

      setTimeout(() => {
        onChangeBackground({
          backgroundImageId: result.backgroundImage?.id,
          backgroundColor: result.backgroundColor,
          backgroundImageAlt: result.backgroundImage?.altAttribute || '',
          mobileBackgroundId: result.mobileBackgroundImage?.id,
          mobileBackgroundImageAlt: result.mobileBackgroundImage?.altAttribute || '',
          // puck results carry resolved urls instead of beaker image ids
          backgroundImageSrc: result.puckBackgroundImageUrl,
          mobileBackgroundImageSrc: result.puckMobileBackgroundImageUrl,
        })
      }, ANIMATION_DURATION)

      analytics.stepEvent('step_interaction_pq_result', step, {
        result_id: result.id,
        result: result.title,
        context_id: quiz.id,
        context: quiz.headline,
      })

      eventEmitter.emit('setPQResult', result.id)
      sessionStorageWrapper.setItem(`${breakId}_pq_result_id`, result.id)
      onEngage()
      onAwardPoints()

      // PUCK-BACKEND-GAP (BR-5451): skip backend result save for puck steps
      if (!isPuck) {
        try {
          saveQuizResult(quiz.id, result.id, questionAnswerSet).catch(e => {
            console.log(e)
          })
        } catch (e) {
          console.log(e)
        }
      }
    }
  }

  // slides send us event data to bubble up
  const handleEvent = (eventName, additionalData) => {
    analytics.stepEvent(eventName, step, additionalData)
  }

  const handleShareClick = ({ platform }) => {
    analytics.stepEvent('step_interaction_pq_share_option', step, {
      result_id: platform,
      result: platform,
    })
  }

  useEffect(() => {
    onChangeBackground({
      backgroundImageId: step.personalityQuiz.questionBackgroundImageId || (step.personalityQuiz.questionBackgroundImage?.id),
      backgroundColor: step.personalityQuiz.questionBackgroundColor,
      backgroundImageAlt: (step.personalityQuiz.questionBackgroundImage?.altAttribute) || '',
      mobileBackgroundId: step.personalityQuiz.questionMobileBackgroundImageId || (step.personalityQuiz.questionMobileBackgroundImage?.id),
      mobileBackgroundImageAlt: (step.personalityQuiz.questionMobileBackgroundImage?.altAttribute) || '',
    })
  }, [
    step.personalityQuiz.questionBackgroundImageId,
    step.personalityQuiz.questionBackgroundColor,
    step.personalityQuiz.questionBackgroundImage?.altAttribute,
    step.personalityQuiz.questionMobileBackgroundImageId,
    step.personalityQuiz.questionMobileBackgroundImage?.altAttribute,
  ])

  return (
    <section
      className={`quiz step-content`}
      // puck (BR-5451): configurable card max width
      style={{
        ...(quiz.puckMaxWidth
          ? {
              maxWidth: quiz.puckMaxWidth,
              width: '100%',
              marginLeft: 'auto',
              marginRight: 'auto',
              // author controls the width explicitly — drop the theme's
              // side padding so cards fill the box edge to edge
              paddingLeft: 0,
              paddingRight: 0,
            }
          : {}),
        ...(quiz.puckContainerBackground
          ? { backgroundColor: quiz.puckContainerBackground }
          : {}),
      }}
    >
      <>
        <CSSTransition
          in={state.showQuestion}
          timeout={ANIMATION_DURATION * 2}
          classNames="foo"
          unmountOnExit={false}
        >
          {state.position < state.questions.length ? (
            <QuestionSlide
              question={question}
              currentPosition={question.position}
              totalQuestions={quiz.questions.length}
              onAnswerClick={handleAnswerClick}
              selectedAnswerId={undefined}
              skipButton={skipButton}
              nextStepButton={nextStepButton}
              backgroundColor={step.personalityQuiz.questionBackgroundColor || '#1a1a1a'}
              backgroundImage={null}
              hideProgressBar={quiz.puckHideProgressBar}
              answerHoverColor={quiz.puckAnswerHoverColor}
            />
          ) : (
            <></>
          )}
        </CSSTransition>

        <CSSTransition
          in={state.showResult}
          timeout={ANIMATION_DURATION * 2}
          classNames="bar"
          appear={false}
        >
          {state.showResult ? (
            <ResultSlide
              isEditorial={false}
              isGuest={isGuest}
              guestPointsEarned={4}
              result={state.result}
              quiz={quiz}
              currentSlide={quiz.questions.length}
              questionCount={quiz.questions.length}
              relatedBreaks={relatedBreaks}
              stepID={step.id}
              onEvent={handleEvent}
              socialShare={state.component}
              skipButton={skipButton}
              nextStepButton={nextStepButton}
            />
          ) : (
            <></>
          )}
        </CSSTransition>
      </>
    </section>
  )
}

export default PersonalityQuizStep
